import React, { useMemo } from 'react';

// Mood color and position mapping - matching screenshot gradient
const MOOD_CONFIG = {
  "Chill": { angle: 315, color: "rgb(59, 130, 246)", emoji: "😌" }, // Blue (top-left)
  "Energetic": { angle: 45, color: "rgb(234, 179, 8)", emoji: "🔥" }, // Yellow (top-right)
  "Romantic": { angle: 135, color: "rgb(239, 68, 68)", emoji: "❤️" }, // Pink/Red (right)
  "Party": { angle: 225, color: "rgb(251, 146, 60)", emoji: "🎉" } // Orange (bottom-left)
};

export const MoodRing = ({ selectedMoods = [], userAvatar, friends = [], onRequestClick }) => {
  // Calculate avatar position based on selected moods
  const avatarPosition = useMemo(() => {
    if (!selectedMoods || selectedMoods.length === 0) {
      // Default position in center
      return { x: 50, y: 50, angle: 0 };
    }

    if (selectedMoods.length === 1) {
      // Position at the single mood's angle
      const mood = selectedMoods[0];
      const config = MOOD_CONFIG[mood];
      if (!config) return { x: 50, y: 50, angle: 0 };

      const angleRad = (config.angle * Math.PI) / 180;
      const radius = 35; // % from center
      const x = 50 + radius * Math.cos(angleRad);
      const y = 50 + radius * Math.sin(angleRad);
      return { x, y, angle: config.angle };
    }

    // Position between two moods
    const mood1 = MOOD_CONFIG[selectedMoods[0]];
    const mood2 = MOOD_CONFIG[selectedMoods[1]];

    if (!mood1 || !mood2) return { x: 50, y: 50, angle: 0 };

    // Calculate midpoint angle
    let angle1 = mood1.angle;
    let angle2 = mood2.angle;

    // Handle angle wrapping (e.g., 315 and 45 should average to 0, not 180)
    if (Math.abs(angle1 - angle2) > 180) {
      if (angle1 > angle2) angle2 += 360;
      else angle1 += 360;
    }

    const midAngle = ((angle1 + angle2) / 2) % 360;
    const angleRad = (midAngle * Math.PI) / 180;
    const radius = 35;
    const x = 50 + radius * Math.cos(angleRad);
    const y = 50 + radius * Math.sin(angleRad);

    return { x, y, angle: midAngle };
  }, [selectedMoods]);

  // Calculate friend avatar positions evenly distributed around the ring
  const friendPositions = useMemo(() => {
    if (!friends || friends.length === 0) return [];

    const radius = 42; // % from center (slightly outside the ring)
    const positions = friends.map((friend, index) => {
      // Distribute friends evenly around the circle
      const angle = (index / friends.length) * 360;
      const angleRad = (angle * Math.PI) / 180;
      const x = 50 + radius * Math.cos(angleRad);
      const y = 50 + radius * Math.sin(angleRad);

      return {
        ...friend,
        x,
        y,
        angle
      };
    });

    return positions;
  }, [friends]);

  return (
    <div className="w-full bg-[#2a2a2a] rounded-lg p-4 mb-4">
      {/* Flex container: Circle on left, content on right */}
      <div className="flex items-center gap-6">
        {/* Mood Ring Circle - Float left */}
        <div className="relative w-48 h-48 flex-shrink-0">
          {/* Gradient ring with conic gradient - thinner padding */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: 'conic-gradient(from 0deg, rgb(59, 130, 246), rgb(236, 72, 153), rgb(251, 146, 60), rgb(234, 179, 8), rgb(59, 130, 246))',
              padding: '4px'
            }}
          >
            {/* Inner dark circle */}
            <div className="w-full h-full rounded-full bg-[#1a1a2e] flex items-center justify-center">
              {/* Inner content area */}
              <div className="w-[65%] h-[65%] rounded-full border border-gray-700/50"></div>
            </div>
          </div>

          {/* User Avatar - positioned based on selected moods */}
          {userAvatar && (
            <div
              className="absolute w-12 h-12 rounded-full border-4 border-white shadow-lg overflow-hidden"
              style={{
                left: `calc(${avatarPosition.x}% - 24px)`,
                top: `calc(${avatarPosition.y}% - 24px)`,
                transition: 'all 0.5s ease-in-out'
              }}
            >
              <img
                src={userAvatar}
                alt="User avatar"
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Friend Avatars - positioned evenly around the ring */}
          {friendPositions.map((friend, index) => (
            <div
              key={friend.id || index}
              className="absolute w-8 h-8 rounded-full border-2 border-white/70 shadow-md overflow-hidden hover:scale-110 transition-transform duration-200"
              style={{
                left: `calc(${friend.x}% - 16px)`,
                top: `calc(${friend.y}% - 16px)`,
                transition: 'all 0.5s ease-in-out'
              }}
              title={friend.displayName}
            >
              {friend.photoURL ? (
                <img
                  src={friend.photoURL}
                  alt={friend.displayName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                  {friend.displayName?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right content: Selected moods and button */}
        <div className="flex-1 flex flex-col justify-center gap-4">
          {/* Selected Moods Display */}
          {selectedMoods && selectedMoods.length > 0 && (
            <div>
              <p className="text-sm text-white/70 mb-2">Your selected mood{selectedMoods.length > 1 ? 's' : ''}:</p>
              <div className="flex gap-2 flex-wrap">
                {selectedMoods.map(mood => {
                  const config = MOOD_CONFIG[mood];
                  return config ? (
                    <span
                      key={mood}
                      className="px-3 py-1 rounded-full text-sm font-semibold text-white"
                      style={{ backgroundColor: config.color }}
                    >
                      {config.emoji} {mood}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {/* Request Button */}
          <div>
            <button
              onClick={onRequestClick}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              Make a Request
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoodRing;
